import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const WEEKEND = [0, 6];

class KjalltShippingInfo extends HTMLElement {
  connectedCallback() {
    this.#renderDate();
    this.#renderCountry();
  }

  #renderDate() {
    const target = this.querySelector('[data-ship-date]');
    if (!target) return;

    const includeWeekend = this.dataset.includeWeekend === 'true';
    const date = new Date();
    let remaining = Number(this.dataset.days) || 0;

    while (remaining > 0) {
      date.setDate(date.getDate() + 1);
      if (includeWeekend || !WEEKEND.includes(date.getDay())) remaining -= 1;
    }
    while (!includeWeekend && WEEKEND.includes(date.getDay())) {
      date.setDate(date.getDate() + 1);
    }

    target.textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }).format(date);
  }

  async #renderCountry() {
    const target = this.querySelector('[data-ship-country]');
    if (!target) return;

    let code = this.dataset.country || '';
    try {
      const response = await fetch('/browsing_context_suggestions.json');
      const data = await response.json();
      code = data?.detected_values?.country?.handle || code;
    } catch {
      // The market country rendered by Liquid stays in place when detection is unavailable.
    }

    const allowed = (this.dataset.allowed || '')
      .split(',')
      .map((value) => value.trim().toUpperCase())
      .filter(Boolean);
    const matches = this.dataset.allowAll === 'true' || allowed.includes(code);
    const template = (matches ? this.dataset.textMatch : this.dataset.textRest) || '';
    const flag = [...code].map((char) => String.fromCodePoint(char.charCodeAt(0) + 127397)).join('');

    target.innerHTML = template.replaceAll('COUNTRY_FLAG', flag).replaceAll('COUNTRY_CODE', code);
  }
}

class KjalltVideoCarousel extends HTMLElement {
  #abort = new AbortController();
  #observer;

  connectedCallback() {
    const { signal } = this.#abort;
    this.addEventListener('click', this.#onClick, { signal });
    this.querySelector('[data-track]')?.addEventListener('scroll', () => this.#updateArrows(), { signal, passive: true });
    this.querySelector('[data-modal]')?.addEventListener('close', this.#onModalClose, { signal });
    window.addEventListener('resize', () => this.#updateArrows(), { signal, passive: true });
    this.#updateArrows();
    this.#observeVideos();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#observer?.disconnect();
  }

  #observeVideos() {
    if (this.dataset.autoplay !== 'true' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const video = entry.target;
          if (!(video instanceof HTMLVideoElement)) continue;
          if (entry.isIntersecting) {
            video.muted = true;
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        }
      },
      { threshold: 0.2 }
    );

    for (const video of this.querySelectorAll('[data-track] video')) this.#observer.observe(video);
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const track = this.querySelector('[data-track]');
    const arrow = event.target.closest('[data-direction]');
    if (arrow && track) {
      track.scrollBy({ left: Number(arrow.getAttribute('data-direction')) * track.clientWidth * 0.8, behavior: 'smooth' });
      return;
    }

    const modal = this.querySelector('[data-modal]');
    if (!(modal instanceof HTMLDialogElement)) return;

    if (event.target.closest('[data-close]') || event.target === modal) {
      modal.close();
      return;
    }

    const opener = event.target.closest('[data-video-src]');
    if (!opener) return;

    const video = modal.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    video.src = opener.getAttribute('data-video-src') || '';
    modal.showModal();
    video.play().catch(() => {});
  };

  #onModalClose = () => {
    const video = this.querySelector('[data-modal] video');
    if (!(video instanceof HTMLVideoElement)) return;
    video.pause();
    video.removeAttribute('src');
    video.load();
  };

  #updateArrows() {
    const track = this.querySelector('[data-track]');
    if (!track) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const previous = this.querySelector('[data-direction="-1"]');
    const next = this.querySelector('[data-direction="1"]');
    if (previous) previous.hidden = maxScroll <= 1 || track.scrollLeft <= 1;
    if (next) next.hidden = maxScroll <= 1 || track.scrollLeft >= maxScroll - 1;
  }
}

class KjalltBundle extends HTMLElement {
  #abort = new AbortController();
  #variants = [];
  #busy = false;

  connectedCallback() {
    const { signal } = this.#abort;
    this.#variants = JSON.parse(this.querySelector('[data-bundle-variants]')?.textContent || '[]');

    this.addEventListener('change', this.#onOwnChange, { signal });
    this.#section?.addEventListener('change', this.#onSectionChange, { signal });
    window.addEventListener('click', this.#onAddClick, { capture: true, signal });

    this.#syncPickersToMain();
    this.#refresh();
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  get #section() {
    return this.closest('.shopify-section');
  }

  get #tiers() {
    return Array.from(this.querySelectorAll('[data-tier]'));
  }

  get #selectedTier() {
    return this.#tiers.find((tier) => tier.querySelector('input[type="radio"]')?.checked) ?? this.#tiers[0];
  }

  #mainOptions() {
    const picker = this.#section?.querySelector('variant-picker');
    return picker?.getAllSelectedOptions?.() ?? [];
  }

  #mainVariant() {
    const selected = this.#mainOptions();
    const names = this.dataset.optionNames ? JSON.parse(this.dataset.optionNames) : [];
    const values = names.map((name) => selected.find((option) => option.name === name)?.value);
    return this.#variants.find((variant) => variant.options.every((value, index) => value === values[index])) ?? this.#variants.find((variant) => variant.available) ?? this.#variants[0];
  }

  #variantForUnit(unit) {
    const values = Array.from(unit.querySelectorAll('select')).map((select) => select.value);
    return this.#variants.find((variant) => variant.options.every((value, index) => value === values[index]));
  }

  #unitsFor(tier) {
    const rows = Array.from(tier.querySelectorAll('[data-unit]'));
    if (rows.length) return rows.map((row) => this.#variantForUnit(row));
    const main = this.#mainVariant();
    return Array.from({ length: Number(tier.dataset.quantity) || 1 }, () => main);
  }

  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'EUR');
  }

  #onOwnChange = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.type === 'radio') this.#clearError();
    this.#refresh();
  };

  #onSectionChange = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('variant-picker')) return;
    this.#syncPickersToMain();
    this.#refresh();
  };

  #syncPickersToMain() {
    for (const { name, value } of this.#mainOptions()) {
      for (const select of this.querySelectorAll('select[data-option-name]')) {
        if (select.dataset.optionName === name) select.value = value;
      }
    }
  }

  #refresh() {
    const selectedTier = this.#selectedTier;
    const main = this.#mainVariant();

    for (const tier of this.#tiers) {
      const isSelected = tier === selectedTier;
      tier.classList.toggle('is-selected', isSelected);

      const pickers = tier.querySelector('[data-pickers]');
      if (pickers) pickers.hidden = !isSelected;

      const units = this.#unitsFor(tier);
      const prices = units.filter(Boolean).map((variant) => variant.price).sort((a, b) => a - b);
      const free = Math.min(Number(tier.dataset.free) || 0, prices.length);
      const saving = prices.slice(0, free).reduce((sum, price) => sum + price, 0);
      const total = prices.reduce((sum, price) => sum + price, 0);

      const price = tier.querySelector('[data-price]');
      if (price) price.textContent = this.#money(total - saving);
      const compare = tier.querySelector('[data-compare]');
      if (compare) compare.textContent = this.#money(total);

      for (const element of tier.querySelectorAll('[data-token-template]')) {
        element.textContent = (element.getAttribute('data-token-template') || '').replaceAll('[saving]', this.#money(saving));
      }

      const image = tier.querySelector('[data-tier-image]');
      if (image instanceof HTMLImageElement && main?.image) image.src = main.image;

      for (const row of tier.querySelectorAll('[data-unit]')) {
        const variant = this.#variantForUnit(row);
        const unitImage = row.querySelector('[data-unit-image]');
        if (unitImage instanceof HTMLImageElement && variant?.image) unitImage.src = variant.image;
      }
    }
  }

  #showError(message) {
    const error = this.querySelector('[data-bundle-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
  }

  #clearError() {
    const error = this.querySelector('[data-bundle-error]');
    if (!error) return;
    error.textContent = '';
    error.hidden = true;
  }

  #onAddClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[ref="addToCartButton"]');
    if (!(button instanceof HTMLButtonElement) || button.closest('sticky-add-to-cart')) return;
    if (button.closest('.shopify-section') !== this.#section) return;

    const tier = this.#selectedTier;
    if (!tier || (Number(tier.dataset.quantity) <= 1 && !tier.querySelector('[data-gift-variant]'))) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (!button.disabled) this.#add(tier, button);
  };

  async #add(tier, button) {
    if (this.#busy) return;

    const units = this.#unitsFor(tier);
    if (units.some((variant) => !variant || !variant.available)) {
      this.#showError(this.dataset.errorUnavailable || '');
      return;
    }

    this.#busy = true;
    this.#clearError();
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const deferred = CartLinesUpdateEvent.createPromise();

    try {
      const current = await this.#fetchCart();
      const grouped = new Map();
      for (const variant of units) grouped.set(variant.id, (grouped.get(variant.id) || 0) + 1);

      const items = Array.from(grouped, ([id, quantity]) => ({ id, quantity }));
      for (const gift of tier.querySelectorAll('[data-gift-variant]')) {
        const id = Number(gift.getAttribute('data-gift-variant'));
        const inCart = current.items.some((item) => item.variant_id === id && item.properties?._kjallt_gift);
        if (id && !inCart) items.push({ id, quantity: 1, properties: { _kjallt_gift: '1' } });
      }

      const sections = Array.from(document.querySelectorAll('cart-items-component'))
        .map((element) => element.getAttribute('data-section-id'))
        .filter(Boolean);

      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
          promise: deferred.promise,
        })
      );

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(',') }),
      });
      const result = await response.json();
      const cart = await this.#fetchCart();
      const didError = Boolean(result.status);

      if (didError) {
        this.dispatchEvent(new CartErrorEvent({ error: result.message || 'Add to cart failed', code: 'INVALID', detail: { description: result.description } }));
        this.#showError(result.description || result.message || this.dataset.errorUnavailable || '');
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'kjallt-bundle',
          itemCount: cart.item_count,
          sections: didError ? undefined : result.sections,
          didError,
        },
      });
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.#showError(this.dataset.errorUnavailable || '');
    } finally {
      this.#busy = false;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }
}

if (!customElements.get('kjallt-shipping-info')) customElements.define('kjallt-shipping-info', KjalltShippingInfo);
if (!customElements.get('kjallt-video-carousel')) customElements.define('kjallt-video-carousel', KjalltVideoCarousel);
if (!customElements.get('kjallt-bundle')) customElements.define('kjallt-bundle', KjalltBundle);
